/**
 * SessionTypesManager Component
 *
 * Manage session types (Lab, Harvest, Substrate, etc.)
 * Allows creating and editing session types with colors.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { useSessionTypes, useTimetableMutations } from '@/hooks/useTimetable'
import { Plus, Edit, Loader2, Tag } from 'lucide-react'

const DEFAULT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Orange
  '#8B5CF6', // Purple
  '#EF4444', // Red
  '#6B7280', // Gray
  '#14B8A6', // Teal
  '#F43F5E', // Rose
]

export default function SessionTypesManager({ open, onOpenChange }) {
  const { sessionTypes, refetch } = useSessionTypes()
  const { createSessionType, updateSessionType, loading } = useTimetableMutations()
  const [showForm, setShowForm] = useState(false)
  const [editingType, setEditingType] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [colour, setColour] = useState(DEFAULT_COLORS[0])
  const [error, setError] = useState(null)

  const handleAddNew = () => {
    setEditingType(null)
    setName('')
    setDescription('')
    setColour(DEFAULT_COLORS[0])
    setError(null)
    setShowForm(true)
  }

  const handleEdit = (type) => {
    setEditingType(type)
    setName(type.name)
    setDescription(type.description || '')
    setColour(type.colour || DEFAULT_COLORS[0])
    setError(null)
    setShowForm(true)
  }

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!colour) {
      setError('Color is required')
      return
    }

    setError(null)

    try {
      const typeData = {
        name: name.trim(),
        description: description.trim() || null,
        colour: colour
      }

      if (editingType) {
        await updateSessionType(editingType.id, typeData)
      } else {
        await createSessionType(typeData)
      }

      refetch()
      setShowForm(false)
    } catch (err) {
      console.error('[SessionTypesManager] Save error:', err)
      setError(err.message || 'Failed to save session type')
    }
  }

  const handleCancel = () => {
    setShowForm(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Manage Session Types
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6 py-4">
          {/* List of existing session types */}
          {!showForm && (
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleAddNew}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Session Type
              </Button>

              {sessionTypes.map(type => (
                <Card key={type.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div
                          className="w-6 h-6 rounded flex-shrink-0 mt-1"
                          style={{ backgroundColor: type.colour || '#9CA3AF' }}
                        />
                        <div className="flex-1">
                          <div className="font-semibold">{type.name}</div>
                          {type.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {type.description}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(type)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {sessionTypes.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No session types defined yet
                </div>
              )}
            </div>
          )}

          {/* Form for adding/editing */}
          {showForm && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="type-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="type-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Lab, Harvest, Substrate..."
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type-description">
                  Description <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="type-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this session type..."
                  rows={2}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Color <span className="text-destructive">*</span>
                </Label>
                <div className="grid grid-cols-8 gap-2">
                  {DEFAULT_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-full aspect-square rounded border-2 transition-all hover:scale-110 ${
                        colour === color ? 'border-foreground ring-2 ring-foreground' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setColour(color)}
                      disabled={loading}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="text"
                    value={colour}
                    onChange={(e) => setColour(e.target.value)}
                    placeholder="#3B82F6"
                    className="font-mono"
                    disabled={loading}
                  />
                  <Input
                    type="color"
                    value={colour}
                    onChange={(e) => setColour(e.target.value)}
                    className="w-16 h-10 cursor-pointer"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={handleCancel} disabled={loading} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={loading} className="flex-1">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingType ? 'Save Changes' : 'Create Type'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {!showForm && (
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
